/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useData } from "../data/DataContext";
import { getCaseStudyBySlug, getRelatedCaseStudies, formatDate } from "../data/blogHelpers";
import { Helmet } from 'react-helmet-async';
import "./BlogPost.css";

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  const answerRef = useRef(null);

  return (
    <div className={`faq-item ${open ? "faq-item--open" : ""}`}>
      <button
        className="faq-item__question"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span>{question}</span>
        <svg
          className="faq-item__icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <div
        className="faq-item__answer"
        ref={answerRef}
        style={{
          maxHeight: open ? answerRef.current?.scrollHeight + "px" : "0px",
        }}
      >
        <p>{answer}</p>
      </div>
    </div>
  );
}

// ─── Author Bio Card — renders at the end of the article ───
function AuthorBio({ blog }) {
  const {
    author,
    authorRole,
    authorImage,
    authorExperience,
    authorExpertise,
    authorBio,
    authorSocial,
  } = blog;

  if (!author) return null;

  return (
    <div className="blog-author-bio">
      <div className="blog-author-bio__avatar-wrap">
        {authorImage ? (
          <img
            src={authorImage}
            alt={author}
            className="blog-author-bio__avatar-img"
          />
        ) : (
          <div className="blog-author-bio__avatar-fallback">
            {author.charAt(0)}
          </div>
        )}
      </div>

      <div className="blog-author-bio__content">
        <p className="blog-author-bio__label">Written by</p>
        <h3 className="blog-author-bio__name">{author}</h3>
        <p className="blog-author-bio__role">
          {authorRole}
          {authorExperience && (
            <span className="blog-author-bio__exp"> · {authorExperience} experience</span>
          )}
        </p>

        {authorBio && <p className="blog-author-bio__text">{authorBio}</p>}

        {authorExpertise?.length > 0 && (
          <div className="blog-author-bio__expertise">
            {authorExpertise.map((skill) => (
              <span key={skill} className="blog-tag">{skill}</span>
            ))}
          </div>
        )}

        <div className="blog-author-bio__footer">
          {authorSocial && (
            <div className="blog-author-bio__social">
              {/* LinkedIn */}
              {authorSocial.linkedin && (
                <a href={authorSocial.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${author} on LinkedIn`}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              )}
              {/* X / Twitter */}
              {authorSocial.twitter && (
                <a href={authorSocial.twitter} target="_blank" rel="noopener noreferrer" aria-label={`${author} on X`}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.26 5.632zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
              )}
              {/* Facebook */}
              {authorSocial.facebook && (
                <a href={authorSocial.facebook} target="_blank" rel="noopener noreferrer" aria-label={`${author} on Facebook`}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
                    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.235 2.686.235v2.97h-1.513c-1.491 0-1.956.93-1.956 1.887v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
                  </svg>
                </a>
              )}
              {/* Instagram */}
              {authorSocial.instagram && (
                <a href={authorSocial.instagram} target="_blank" rel="noopener noreferrer" aria-label={`${author} on Instagram`}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                </a>
              )}
              {/* YouTube */}
              {authorSocial.youtube && (
                <a href={authorSocial.youtube} target="_blank" rel="noopener noreferrer" aria-label={`${author} on YouTube`}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
              )}
            </div>
          )}

          {/* CTA button → redirects to /team page */}
          <Link to="/team" className="blog-author-bio__cta-btn">
            Meet Our CFO Team
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── Results / Metrics strip — case-study specific ───
function ResultsStrip({ results }) {
  if (!results?.length) return null;

  return (
    <div className="case-study-results-strip">
      {results.map((r) => (
        <div key={r.label} className="case-study-results-strip__item">
          <strong>{r.value}</strong>
          <span>{r.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Walk up the DOM to find the element that is actually scrolling ───
function getScrollParent(el) {
  if (!el) return window;
  let node = el.parentElement;
  while (node && node !== document.body) {
    const { overflow, overflowY } = getComputedStyle(node);
    if (/(auto|scroll)/.test(overflow + overflowY) && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return window;
}

function smoothScrollTo(container, top) {
  if (container === window) {
    window.scrollTo({ top, behavior: "smooth" });
  } else {
    container.scrollTo({ top, behavior: "smooth" });
  }
}

function getOffsetRelativeTo(target, container) {
  if (container === window) {
    return target.getBoundingClientRect().top + window.scrollY;
  }
  return (
    target.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop
  );
}

export default function CaseStudyPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const pageRef = useRef(null);
  const { caseStudies, loading } = useData();

  const handleContactClick = (e) => {
    e.preventDefault();
    const scrollToContact = () => {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (window.location.pathname === "/") {
      scrollToContact();
      return;
    }
    navigate("/");
    setTimeout(scrollToContact, 300);
  };

  const blog = !loading ? getCaseStudyBySlug(caseStudies, slug) : null;
  const related = blog ? getRelatedCaseStudies(caseStudies, slug, 3) : [];

  const [progress, setProgress] = useState(0);
  const [toc, setToc] = useState([]);

useEffect(() => {
    if (!loading && !blog) navigate("/case-studies", { replace: true });
  }, [blog, loading, navigate]);

  useEffect(() => {
    const container = getScrollParent(pageRef.current);
    smoothScrollTo(container, 0);
  }, [slug]);

  useEffect(() => {
    const tid = setTimeout(() => {
      const container = getScrollParent(pageRef.current);

      const onScroll = () => {
        let scrolled, height;
        if (container === window) {
          const doc = document.documentElement;
          scrolled = doc.scrollTop || document.body.scrollTop;
          height = doc.scrollHeight - doc.clientHeight;
        } else {
          scrolled = container.scrollTop;
          height = container.scrollHeight - container.clientHeight;
        }
        setProgress(height > 0 ? Math.round((scrolled / height) * 100) : 0);
      };

      container.addEventListener("scroll", onScroll, { passive: true });
      return () => container.removeEventListener("scroll", onScroll);
    }, 100);

    return () => clearTimeout(tid);
  }, [slug]);

  useEffect(() => {
    if (!blog) return;

    const raf = requestAnimationFrame(() => {
      setTimeout(() => {
        const article = document.querySelector(".blog-post__content");
        if (!article) return;

        const headings = article.querySelectorAll("h2, h3");
        const items = [];

        headings.forEach((h, i) => {
          const id = `section-${i}`;
          h.id = id;
          items.push({
            id,
            text: h.textContent.trim(),
            level: h.tagName.toLowerCase(),
          });
        });

        setToc(items);
      }, 0);
    });

    return () => cancelAnimationFrame(raf);
  }, [blog, slug]);

  const scrollToSection = useCallback((id) => {
    const target = document.getElementById(id);
    if (!target) return;
    const OFFSET = 80;
    const container = getScrollParent(target);
    const relativeTop = getOffsetRelativeTo(target, container);
    smoothScrollTo(container, relativeTop - OFFSET);
  }, []);

  useEffect(() => {
    if (!blog) return;
    const article = document.querySelector(".blog-post__content");
    if (!article) return;

    const handleClick = (e) => {
      if (!e.target.closest(".blog-inline-cta-btn")) return;
      e.preventDefault();
      const scrollToContact = () => {
        const el = document.getElementById("contact");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      if (window.location.pathname === "/") {
        scrollToContact();
      } else {
        navigate("/");
        setTimeout(scrollToContact, 300);
      }
    };

    article.addEventListener("click", handleClick);
    return () => article.removeEventListener("click", handleClick);
  }, [blog, navigate]);

  if (loading) return null;
  if (!blog) return null;

  const faqSchema = blog.faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: blog.faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        })),
      }
    : null;

  return (
    <>
      <Helmet>
        <title>{blog.title} | CFO Bridge UAE</title>
        <meta name="description" content={blog.excerpt} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.excerpt} />
        <meta property="og:image" content={blog.image} />
        <meta property="og:type" content="article" />
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Helmet>

      <div className="blog-post-page" ref={pageRef}>
        <div className="reading-progress" style={{ width: `${progress}%` }} />

        <div className="blog-post__breadcrumb">
          <div className="blog-post__breadcrumb-inner">
            <Link to="/">Home</Link>
            <span>›</span>
            <Link to="/case-studies">Case Studies</Link>
            <span>›</span>
            <span>{blog.title}</span>
          </div>
        </div>

        <section className="blog-post__hero">
          <div className="blog-post__hero-inner">
            <div className="blog-post__hero-meta">
              {blog.industry && <span className="blog-cat-badge">{blog.industry}</span>}
              <span className="blog-post__hero-dot" />
              <span>{formatDate(blog.date)}</span>
              <span className="blog-post__hero-dot" />
              <span>{blog.readTime}</span>
            </div>
            <h1 className="blog-post__hero-title">{blog.title}</h1>
            <p className="blog-post__hero-excerpt">{blog.excerpt}</p>

            {blog.author && (
              <div className="blog-post__author">
                <div className="blog-post__author-avatar">
                  {blog.authorAvatar ? (
                    <img
                      src={blog.authorAvatar}
                      alt={blog.author}
                      className="blog-post__author-avatar-img"
                    />
                  ) : (
                    blog.author.charAt(0)
                  )}
                </div>
                <div>
                  <p className="blog-post__author-name">{blog.author}</p>
                  <p className="blog-post__author-role">{blog.authorRole}</p>
                </div>
              </div>
            )}
          </div>
        </section>

        <div className="blog-post__cover">
          <img src={blog.image} alt={blog.title} />
        </div>

        {/* ── Results / Metrics Strip ── */}
        <ResultsStrip results={blog.results} />

        <div className="blog-post__body">
          <div className="blog-post__layout">
            <article className="blog-post__article">
              <div className="blog-post__tags">
                {blog.tags?.map((t) => (
                  <span key={t} className="blog-tag">{t}</span>
                ))}
              </div>

              <div
                className="blog-post__content"
                dangerouslySetInnerHTML={{ __html: blog.content }}
                onClick={(e) => {
                  const btn = e.target.closest(".blog-inline-cta-btn");
                  if (!btn) return;
                  e.preventDefault();
                  const scrollToContact = () => {
                    const el = document.getElementById("contact");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  };
                  if (window.location.pathname === "/") {
                    scrollToContact();
                  } else {
                    navigate("/");
                    setTimeout(scrollToContact, 300);
                  }
                }}
              />

              {/* ── End-of-article CTA Banner ── */}
              <div className="blog-end-cta">
                <div className="blog-end-cta__text">
                  <h3>Want results like this for your business?</h3>
                  <p>Talk to a fractional CFO who's solved this before — no commitment, no pressure.</p>
                </div>
                <div className="blog-end-cta__actions">
                  <button className="blog-end-cta__btn blog-end-cta__btn--primary" onClick={handleContactClick}>
                    Consult with our Virtual CFO Now
                  </button>
                  <button className="blog-end-cta__btn blog-end-cta__btn--secondary" onClick={handleContactClick}>
                    Request a Call Back
                  </button>
                </div>
              </div>

              {blog.faqs?.length > 0 && (
                <div className="blog-post__faq">
                  <h2 className="blog-post__faq-title">Frequently Asked Questions</h2>
                  <div className="blog-post__faq-list">
                    {blog.faqs.map((faq, i) => (
                      <FaqItem key={i} question={faq.question} answer={faq.answer} />
                    ))}
                  </div>
                </div>
              )}

              {/* ── Author Bio ── */}
              <AuthorBio blog={blog} />

              <div className="blog-post__share">
                <p className="blog-post__share-label">Share this case study</p>
                <div className="blog-post__share-btns">
                  <a
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(blog.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn share-btn--linkedin"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    LinkedIn
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(blog.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn share-btn--twitter"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.26 5.632zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    X (Twitter)
                  </a>
                  <button
                    className="share-btn share-btn--copy"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      alert("Link copied!");
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                    Copy Link
                  </button>
                </div>
              </div>
            </article>

            <aside className="blog-post__sidebar">
              {toc.length > 0 && (
                <div className="blog-sidebar-card">
                  <h3 className="blog-sidebar-card__title">In This Case Study</h3>
                  <nav className="blog-toc">
                    {toc.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`blog-toc__item blog-toc__item--${item.level}`}
                      >
                        {item.text}
                      </button>
                    ))}
                  </nav>
                </div>
              )}

              <div className="blog-sidebar-card blog-sidebar-card--cta">
                <h3>Need a CFO for your UAE business?</h3>
                <p>Our fractional CFO team provides hands-on financial leadership across Dubai, Abu Dhabi, and the GCC.</p>
                <button onClick={handleContactClick} className="blog-sidebar-cta-btn">
                  Get in Touch
                </button>
              </div>

              {blog.tags?.length > 0 && (
                <div className="blog-sidebar-card">
                  <h3 className="blog-sidebar-card__title">Tags</h3>
                  <div className="blog-sidebar-tags">
                    {blog.tags.map((t) => (
                      <span key={t} className="blog-tag">{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>

          {related.length > 0 && (
            <section className="blog-related">
              <h2 className="blog-related__title">Related Case Studies</h2>
              <div className="blog-related__grid">
                {related.map((b) => (
                  <Link key={b.id} to={`/case-studies/${b.id}`} className="blog-card blog-related__card">
                    <div className="blog-card__img-wrap">
                      <img src={b.image} alt={b.title} className="blog-card__img" />
                      {b.industry && <span className="blog-card__cat">{b.industry}</span>}
                    </div>
                    <div className="blog-card__body">
                      <div className="blog-card__meta">
                        <span>{formatDate(b.date)}</span>
                        <span className="blog-sep">·</span>
                        <span>{b.readTime}</span>
                      </div>
                      <h3 className="blog-card__title">{b.title}</h3>
                      <p className="blog-card__excerpt">{b.excerpt}</p>
                      <div className="blog-card__footer">
                        <span className="blog-featured__cta">
                          Read Case Study
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                          </svg>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}